import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StripeService } from '@galaxy/billing';
import { TranslateModule } from '@ngx-translate/core';
import { EditPaymentMethodDialogComponent } from '@vendasta/billing-ui';
import { combineLatest, Observable } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { BillingSettingsService } from './billing-settings.service';

@Component({
  selector: 'app-payment-info',
  styleUrls: ['./payment-info.component.scss'],
  templateUrl: './payment-info.component.html',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatProgressSpinnerModule],
})
export class PaymentInfoComponent implements OnInit {
  @Input() editable = false;
  @Input() merchantId: string;
  data$: Observable<any>;
  stripeKey$: Observable<string>;

  constructor(
    private billingSettingsService: BillingSettingsService,
    private dialog: MatDialog,
    private stripeService: StripeService,
  ) {}

  ngOnInit(): void {
    if (!this.billingSettingsService.merchantId) {
      this.billingSettingsService.merchantId = this.merchantId;
    }
    this.billingSettingsService.loadMerchantCreditCards();
    this.billingSettingsService.loadMerchantDetails();

    this.data$ = combineLatest([
      this.billingSettingsService.paymentDetailsInfo,
      this.billingSettingsService.loading,
      this.billingSettingsService.isPaymentDetailsConfigured,
      this.billingSettingsService.isMerchantConfigured,
    ]).pipe(
      map(([paymentInfo, loading, paymentConfigured, merchantConfigured]) => {
        let paymentClass = '';
        if (paymentConfigured) {
          paymentClass = this.getPaymentFontClass(paymentInfo.cardType.value);
        }

        return { paymentInfo, loading, paymentConfigured, merchantConfigured, paymentClass };
      }),
    );

    this.stripeKey$ = this.stripeService.getPublicKeyByMerchant(this.merchantId);
  }

  openEditModal(): void {
    this.stripeKey$
      .pipe(
        withLatestFrom(this.billingSettingsService.paymentDetailsInfo),
        map(([stripeKey, card]) => {
          const dialogRef = this.dialog.open(EditPaymentMethodDialogComponent, {
            width: '500px',
            data: {
              merchantId: this.merchantId,
              stripePublicKey: stripeKey,
              recaptchaKey: '6Lf0gKwUAAAAAMiDLNbVfgyFN8fzT3KDSkWeC92d',
              requireRecaptcha: true,
              paymentCard: card !== this.billingSettingsService.emptyPaymentDetails ? card : null,
            },
          });
          dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((_) => {
              this.billingSettingsService.loadMerchantCreditCards();
            });
        }),
        take(1),
      )
      .subscribe();
  }

  getPaymentFontClass(cardType: string): string {
    const cardBrandToPfClass = {
      Visa: 'pf-visa',
      Mastercard: 'pf-mastercard',
      'American Express': 'pf-american-express',
      Discover: 'pf-discover',
      'Diners Club': 'pf-diners',
      JCB: 'pf-jcb',
      Unknown: 'pf-credit-card',
    };
    return Object.prototype.hasOwnProperty.call(cardBrandToPfClass, cardType)
      ? cardBrandToPfClass[cardType]
      : 'pf-credit-card';
  }
}
